/**
 * audio.js —— 参考 demo 事件音 + 程序化落爻磬音。
 *
 * 召阵、阴阳显现、罗盘展开、变卦和收回直接使用用户提供录屏中的 demo13 / 15 /
 * 19 / 21 / 36；Demo.1 承接第六爻定格到成卦显现。六次落爻仍用
 * Web Audio 非谐波磬音，保持每一爻都能辨位。
 * demo 片段只经过切点淡入淡出和播放响度补偿，不改旋律、音调与空间尾音。
 */

export const REFERENCE_NOTES = Object.freeze([
  Object.freeze({ frequency: 522.948, duration: 0.664, volume: 0.26 }),
  Object.freeze({ frequency: 587.509, duration: 0.494, volume: 0.30 }),
  Object.freeze({ frequency: 659.942, duration: 0.444, volume: 0.15 }),
  Object.freeze({ frequency: 784.248, duration: 0.350, volume: 0.18 }),
  Object.freeze({ frequency: 879.592, duration: 0.543, volume: 0.25 }),
  Object.freeze({ frequency: 1046.885, duration: 0.524, volume: 0.32 })
]);

/** [实测] 2026-09-01：落爻磬音整体提高约 55%，不改变六爻之间的相对强弱。 */
export const CAST_VOLUME_SCALE = 2.52;

export const REFERENCE_CHORD = Object.freeze([
  Object.freeze({ frequency: 523.141, duration: 0.72, volume: 0.25 }),
  Object.freeze({ frequency: 660.032, duration: 0.72, volume: 0.18 }),
  Object.freeze({ frequency: 784.218, duration: 0.66, volume: 0.12 }),
  Object.freeze({ frequency: 1046.461, duration: 0.74, volume: 0.35 })
]);

const cue = (frequency, offset, duration, volume) => Object.freeze({
  frequency, offset, duration, volume
});

/** 参考录屏中稳定可分离的事件旋律；只换音色，不改音高。 */
export const REFERENCE_CUES = Object.freeze({
  awaken: Object.freeze([
    cue(783.991, 0.000, 0.42, 0.17),
    cue(987.767, 0.125, 0.42, 0.15),
    cue(1174.659, 0.255, 0.46, 0.13),
    cue(1567.982, 0.390, 0.62, 0.16)
  ]),
  planeExpand: Object.freeze([
    cue(523.251, 0.000, 0.52, 0.13),
    cue(659.255, 0.185, 0.50, 0.12),
    cue(783.991, 0.375, 0.52, 0.12),
    cue(1046.502, 0.590, 0.58, 0.13),
    cue(1318.510, 0.820, 0.66, 0.14)
  ]),
  reset: Object.freeze([
    cue(783.991, 0.000, 0.34, 0.13),
    cue(987.767, 0.120, 0.34, 0.12),
    cue(1174.659, 0.245, 0.36, 0.11),
    cue(1567.982, 0.365, 0.58, 0.14)
  ])
});

/**
 * 2026-08-30 录屏时间线中的原声片段。volume 只补偿各 demo 在录屏里的电平差。
 * demo15 保留完整阴阳显现音；新动作通过统一抢占机制立即截断前音，不再排队等待。
 */
export const DEMO_SAMPLES = Object.freeze({
  appSummon: Object.freeze({
    id: 'demo13', url: 'audio/demo13.wav', duration: 2.80, volume: 1.45
  }),
  yinYang: Object.freeze({
    id: 'demo15', url: 'audio/demo15.wav', duration: 2.60, volume: 1.45
  }),
  expand: Object.freeze({
    id: 'demo19', url: 'audio/demo19.wav', duration: 3.23, volume: 1.45
  }),
  formation: Object.freeze({
    id: 'demo1', url: 'audio/demo1.wav', duration: 4.10, volume: 1.45
  }),
  transform: Object.freeze({
    id: 'demo21', url: 'audio/demo21.wav', duration: 2.95, volume: 2.00
  }),
  retract: Object.freeze({
    id: 'demo36', url: 'audio/demo36.wav', duration: 4.35, volume: 1.45
  })
});

const INSTRUMENTS = Object.freeze({
  // 磬：基音清楚、少量非谐波石质泛音，适合逐爻辨位。
  qing: Object.freeze([
    Object.freeze({ ratio: 1.000, gain: 1.00, decay: 1.00, detune: 0 }),
    Object.freeze({ ratio: 2.010, gain: 0.23, decay: 0.61, detune: 2 }),
    Object.freeze({ ratio: 2.930, gain: 0.105, decay: 0.39, detune: -3 }),
    Object.freeze({ ratio: 4.170, gain: 0.045, decay: 0.25, detune: 5 })
  ]),
  // 编钟：非谐波模态比磬密，承担成卦和弦。
  bianzhong: Object.freeze([
    Object.freeze({ ratio: 1.000, gain: 1.00, decay: 1.00, detune: 0 }),
    Object.freeze({ ratio: 1.490, gain: 0.20, decay: 0.83, detune: -2 }),
    Object.freeze({ ratio: 2.090, gain: 0.16, decay: 0.65, detune: 3 }),
    Object.freeze({ ratio: 2.560, gain: 0.09, decay: 0.48, detune: -4 }),
    Object.freeze({ ratio: 3.020, gain: 0.07, decay: 0.38, detune: 4 }),
    Object.freeze({ ratio: 4.180, gain: 0.035, decay: 0.27, detune: 7 })
  ]),
  // 云锣：明亮而轻，用于唤醒与展开的上行音型。
  yunluo: Object.freeze([
    Object.freeze({ ratio: 1.000, gain: 1.00, decay: 1.00, detune: 0 }),
    Object.freeze({ ratio: 2.000, gain: 0.31, decay: 0.67, detune: 2 }),
    Object.freeze({ ratio: 3.010, gain: 0.12, decay: 0.44, detune: -2 }),
    Object.freeze({ ratio: 4.030, gain: 0.055, decay: 0.30, detune: 4 })
  ]),
  // 三清铃：高频金属模态密集、尾音轻颤，只用于黑场召阵的第一声。
  sanqingBell: Object.freeze([
    Object.freeze({ ratio: 1.000, gain: 1.00, decay: 1.00, detune: 0 }),
    Object.freeze({ ratio: 2.010, gain: 0.38, decay: 0.86, detune: 4 }),
    Object.freeze({ ratio: 2.720, gain: 0.27, decay: 0.72, detune: -6 }),
    Object.freeze({ ratio: 3.980, gain: 0.18, decay: 0.58, detune: 7 }),
    Object.freeze({ ratio: 5.420, gain: 0.105, decay: 0.43, detune: -8 }),
    Object.freeze({ ratio: 6.790, gain: 0.060, decay: 0.31, detune: 10 })
  ]),
  // 低锣模态只作空间与阶段边界，不赋予吉凶含义。
  gong: Object.freeze([
    Object.freeze({ ratio: 1.000, gain: 1.00, decay: 1.00, detune: 0 }),
    Object.freeze({ ratio: 1.370, gain: 0.48, decay: 0.91, detune: -5 }),
    Object.freeze({ ratio: 1.910, gain: 0.31, decay: 0.76, detune: 4 }),
    Object.freeze({ ratio: 2.570, gain: 0.20, decay: 0.62, detune: -7 }),
    Object.freeze({ ratio: 3.130, gain: 0.12, decay: 0.48, detune: 7 }),
    Object.freeze({ ratio: 4.210, gain: 0.06, decay: 0.34, detune: -3 })
  ])
});

const AUDIO = Object.freeze({
  masterGain: 0.46,
  minimumGain: 0.0001,
  scheduleAhead: 0.012,
  interruptFade: 0.012,
  muteTimeConstant: 0.025,
  storageKey: 'liuyao-muted',
  gestureToneDuration: 0.12,
  gestureToneFrequency: 783.991,
  gestureToneGain: 0.055,
  gestureToneThrottleMs: 450,
  sampleGap: 0.08,
  previewVolumeRatio: 0.82,
  castPan: 0.11,
  modalAttack: 0.007,
  modalReleaseTail: 0.06,
  modalTailScale: 1.28,
  strikeDuration: 0.026,
  strikeGain: 0.075,
  strikeFrequency: 2450,
  strikeQ: 0.72,
  reverbSeconds: 3.40,
  reverbDecay: 2.35,
  reverbPreDelay: 0.064,
  dryGain: 0.82,
  wetGain: 0.42,
  echoDelay: 0.185,
  echoFeedback: 0.22,
  echoGain: 0.13,
  compressorThreshold: -12,
  compressorKnee: 18,
  compressorRatio: 4,
  compressorAttack: 0.004,
  compressorRelease: 0.24,
  awakenPlaneDelay: 0.72,
  sanqingStrikeFrequency: 4200,
  sanqingStrikeGain: 0.035,
  sanqingEchoDelay: 0.072,
  formedDelay: 0.85,
  formedGongFrequency: 86.13,
  formedGongDuration: 1.35,
  vortexGongFrequency: 72.4,
  vortexGongDuration: 1.75,
  collapseGongFrequency: 96.9,
  collapseGongDuration: 0.92,
  windDuration: 1.35,
  windGain: 0.075,
  windStartFrequency: 360,
  windEndFrequency: 3100,
  haloStartFrequency: 1800,
  haloEndFrequency: 7200,
  haloDuration: 1.75,
  haloGain: 0.026,
  collapseWindStart: 2500,
  collapseWindEnd: 280,
  resetGongFrequency: 107.67,
  resetGongDuration: 0.88,
  randomSeed: 0x20260828
});

/**
 * 返回参考录屏中六个落爻音。result 只补充爻值/动静元数据，不改变音高；
 * 音高在这里是交互反馈，不解释为吉凶或五行强弱。
 */
export function melodyFor(result = null) {
  return REFERENCE_NOTES.map((note, index) => ({
    position: index + 1,
    value: result?.yao?.[index]?.value ?? null,
    moving: Boolean(result?.yao?.[index]?.moving),
    instrument: 'qing',
    ...note
  }));
}

function storedMuted() {
  try {
    return globalThis.localStorage?.getItem(AUDIO.storageKey) === '1';
  } catch {
    return false;
  }
}

function deterministicNoise(length) {
  const samples = new Float32Array(length);
  let state = AUDIO.randomSeed >>> 0;
  for (let i = 0; i < length; i += 1) {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    samples[i] = ((state >>> 0) / 0xffffffff) * 2 - 1;
  }
  return samples;
}

export class LiuyaoAudio {
  constructor({ forceMuted = false } = {}) {
    this.muted = forceMuted || storedMuted();
    this.context = null;
    this.input = null;
    this.sampleInput = null;
    this.master = null;
    this._noiseCache = new Map();
    this._sampleData = new Map();
    this._sampleBuffers = new Map();
    this._sampleLoadPromise = null;
    this._sampleDecodePromise = null;
    this._pendingSamples = [];
    this._nextSampleAt = 0;
    this._appSummonPlayed = false;
    this._actionStarted = false;
    this._actionGraph = null;
    this._activeSources = new Set();
    this._reverbBuffer = null;
    this._resumePromise = null;
    this._gesturePulse = {
      attempted: false,
      succeeded: false,
      confirmation: false,
      at: null,
      error: null
    };
    this._lastGestureToneAt = 0;
    this._lastPlayback = null;
  }

  get available() {
    return Boolean(globalThis.AudioContext || globalThis.webkitAudioContext);
  }

  get unlocked() {
    return this.context?.state === 'running';
  }

  get status() {
    if (this.muted) return 'muted';
    return this.unlocked ? 'ready' : 'locked';
  }

  get diagnostics() {
    return {
      contextState: this.context?.state || 'none',
      decodedSamples: this._sampleBuffers.size,
      totalSamples: Object.keys(DEMO_SAMPLES).length,
      gesturePulse: this._gesturePulse,
      lastPlayback: this._lastPlayback
    };
  }

  _recordPlayback(call, result, returnValue = false) {
    this._lastPlayback = {
      at: new Date().toISOString().slice(11, 23) + 'Z',
      call,
      result
    };
    return returnValue;
  }

  _makeReverb(context) {
    if (!this._reverbBuffer) {
      const length = Math.round(context.sampleRate * AUDIO.reverbSeconds);
      const impulse = context.createBuffer(2, length, context.sampleRate);
      const noise = deterministicNoise(length * 2);
      for (let channel = 0; channel < 2; channel += 1) {
        const data = impulse.getChannelData(channel);
        for (let i = 0; i < length; i += 1) {
          const time = i / context.sampleRate;
          const reflectionPeriod = Math.round(context.sampleRate * 0.037);
          const earlyReflection = i % reflectionPeriod === 0 ? 0.18 : 0;
          data[i] = (noise[i * 2 + channel] * 0.32 + earlyReflection)
            * Math.exp(-AUDIO.reverbDecay * time);
        }
      }
      this._reverbBuffer = impulse;
    }
    const convolver = context.createConvolver();
    convolver.buffer = this._reverbBuffer;
    return convolver;
  }

  _createActionGraph({ fadeIn = false } = {}) {
    const context = this.context;
    if (!context || !this.master) return null;
    const input = context.createGain();
    const sampleInput = context.createGain();
    const dry = context.createGain();
    const wet = context.createGain();
    const preDelay = context.createDelay(AUDIO.reverbPreDelay);
    const echo = context.createDelay(AUDIO.echoDelay);
    const echoFeedback = context.createGain();
    const echoWet = context.createGain();
    const compressor = context.createDynamicsCompressor();
    const output = context.createGain();
    const reverb = this._makeReverb(context);

    dry.gain.value = AUDIO.dryGain;
    wet.gain.value = AUDIO.wetGain;
    preDelay.delayTime.value = AUDIO.reverbPreDelay;
    echo.delayTime.value = AUDIO.echoDelay;
    echoFeedback.gain.value = AUDIO.echoFeedback;
    echoWet.gain.value = AUDIO.echoGain;
    compressor.threshold.value = AUDIO.compressorThreshold;
    compressor.knee.value = AUDIO.compressorKnee;
    compressor.ratio.value = AUDIO.compressorRatio;
    compressor.attack.value = AUDIO.compressorAttack;
    compressor.release.value = AUDIO.compressorRelease;

    input.connect(dry);
    dry.connect(compressor);
    input.connect(preDelay);
    preDelay.connect(reverb);
    reverb.connect(wet);
    wet.connect(compressor);
    input.connect(echo);
    echo.connect(echoFeedback);
    echoFeedback.connect(echo);
    echo.connect(echoWet);
    echoWet.connect(compressor);
    compressor.connect(output);
    // 参考 demo 已自带空间与混响，与程序音共用本次动作的可中断输出。
    sampleInput.connect(output);
    output.connect(this.master);

    const now = context.currentTime;
    if (fadeIn) {
      output.gain.setValueAtTime(AUDIO.minimumGain, now);
      output.gain.linearRampToValueAtTime(1, now + AUDIO.interruptFade);
    } else {
      output.gain.value = 1;
    }
    this.input = input;
    this.sampleInput = sampleInput;
    this._actionGraph = { input, sampleInput, output };
    return this._actionGraph;
  }

  _trackSource(source) {
    if (!source) return source;
    this._activeSources.add(source);
    const cleanup = () => this._activeSources.delete(source);
    if (typeof source.addEventListener === 'function') source.addEventListener('ended', cleanup, { once: true });
    else source.onended = cleanup;
    return source;
  }

  /** 新动作抢占音频：声源、混响和回声尾音都随上一动作一起截断。 */
  _beginAction() {
    if (!this.context || !this.master || typeof this.context.createGain !== 'function') return false;
    this._actionStarted = true;
    this._pendingSamples.length = 0;
    this._nextSampleAt = 0;
    const now = this.context.currentTime;
    const oldGraph = this._actionGraph;
    if (oldGraph?.output?.gain) {
      const gain = oldGraph.output.gain;
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(Math.max(AUDIO.minimumGain, gain.value), now);
      gain.linearRampToValueAtTime(AUDIO.minimumGain, now + AUDIO.interruptFade);
    }
    this._activeSources.forEach((source) => {
      try { source.stop(now + AUDIO.interruptFade); } catch { /* 已结束 */ }
    });
    this._activeSources.clear();
    this._createActionGraph({ fadeIn: Boolean(oldGraph) });
    if (oldGraph?.output) {
      globalThis.setTimeout?.(() => {
        try { oldGraph.output.disconnect(); } catch { /* 已断开 */ }
      }, Math.ceil(AUDIO.interruptFade * 1000) + 8);
    }
    return true;
  }

  _ensureContext() {
    if (this.context || !this.available) return this.context;
    const Context = globalThis.AudioContext || globalThis.webkitAudioContext;
    const context = new Context();
    const master = context.createGain();
    master.gain.value = this.muted ? AUDIO.minimumGain : AUDIO.masterGain;
    master.connect(context.destination);

    this.context = context;
    this.master = master;
    this._createActionGraph();
    return context;
  }

  async preload() {
    if (this._sampleLoadPromise) return this._sampleLoadPromise;
    if (typeof globalThis.fetch !== 'function' || !globalThis.document?.baseURI) return false;
    this._sampleLoadPromise = Promise.all(Object.values(DEMO_SAMPLES).map(async (sample) => {
      const url = new URL(sample.url, globalThis.document.baseURI);
      const response = await globalThis.fetch(url);
      if (!response.ok) throw new Error(`${sample.id}: HTTP ${response.status}`);
      this._sampleData.set(sample.id, await response.arrayBuffer());
    })).then(() => true).catch((error) => {
      console.warn('[scene:audio-samples]', error);
      return false;
    });
    return this._sampleLoadPromise;
  }

  async _decodeSamples() {
    if (this._sampleDecodePromise) return this._sampleDecodePromise;
    if (!this.context) return false;
    this._sampleDecodePromise = (async () => {
      await this.preload();
      const undecoded = Object.values(DEMO_SAMPLES).filter(
        (sample) => this._sampleData.has(sample.id) && !this._sampleBuffers.has(sample.id)
      );
      await Promise.all(undecoded.map(async (sample) => {
        const encoded = this._sampleData.get(sample.id);
        const buffer = await this.context.decodeAudioData(encoded.slice(0));
        this._sampleBuffers.set(sample.id, buffer);
      }));
      return this._sampleBuffers.size === Object.keys(DEMO_SAMPLES).length;
    })();
    return this._sampleDecodePromise;
  }

  _flushPendingSamples() {
    if (!this.context || this.context.state !== 'running') return;
    const pending = this._pendingSamples.splice(0);
    pending.forEach(({ key, serial }) => this.playSample(key, { serial }));
  }

  async _resumeContext(context) {
    // iOS Safari occasionally resolves resume() while leaving the context
    // suspended. Retry once while the original user activation is still live.
    for (let attempt = 0; attempt < 2 && context.state !== 'running'; attempt += 1) {
      await context.resume();
    }
    return context.state === 'running';
  }

  /**
   * Must be called directly from a trusted gesture handler. There is
   * deliberately no await between resume(), buffer creation and start(0):
   * iOS Safari closes its media-activation window when the stack unwinds.
   */
  unlockFromGesture({ audible = !this.muted } = {}) {
    const context = this._ensureContext();
    const at = new Date().toISOString().slice(11, 23) + 'Z';
    this._gesturePulse = {
      attempted: true,
      succeeded: false,
      confirmation: false,
      at,
      error: null
    };
    if (!context) {
      this._gesturePulse.error = 'no-context';
      return false;
    }

    let resumePromise = null;
    try {
      resumePromise = context.resume();
      const source = context.createBufferSource();
      source.buffer = context.createBuffer(1, 1, 22050);
      source.connect(context.destination);
      source.start(0);
      this._gesturePulse.succeeded = true;

      // iOS 有时会对 1 帧全静音 buffer 视而不见。紧接着在同一手势
      // 同步调用栈内再启动一段很短的真实音频，既强制打通扬声器，也让
      // 用户立即知道音效已开。这之前同样不能有异步等待。
      const nowMs = Date.now();
      if (audible && nowMs - this._lastGestureToneAt >= AUDIO.gestureToneThrottleMs) {
        const sampleRate = context.sampleRate || 22050;
        const frameCount = Math.max(1, Math.round(sampleRate * AUDIO.gestureToneDuration));
        const confirmationBuffer = context.createBuffer(1, frameCount, sampleRate);
        const channel = confirmationBuffer.getChannelData(0);
        for (let frame = 0; frame < frameCount; frame += 1) {
          const progress = frame / frameCount;
          const envelope = Math.sin(Math.PI * progress) ** 2;
          channel[frame] = Math.sin(
            2 * Math.PI * AUDIO.gestureToneFrequency * frame / sampleRate
          ) * envelope * AUDIO.gestureToneGain;
        }
        const confirmationSource = context.createBufferSource();
        confirmationSource.buffer = confirmationBuffer;
        confirmationSource.connect(context.destination);
        confirmationSource.start(0);
        this._lastGestureToneAt = nowMs;
        this._gesturePulse.confirmation = true;
      }
    } catch (error) {
      this._gesturePulse.error = error?.name || error?.message || String(error);
    }

    // Promise handling and WAV decoding intentionally happen only after the
    // synchronous source.start(0) call above.
    if (resumePromise?.catch) {
      void resumePromise.catch((error) => {
        console.warn('[scene:audio-gesture-resume]', error);
      });
    }
    this._prepareSamples();
    return this._gesturePulse.succeeded;
  }

  _playReadySounds() {
    if (!this.context || this.context.state !== 'running' || this.muted) return;
    const summon = DEMO_SAMPLES.appSummon;
    if (!this._appSummonPlayed && !this._actionStarted && this._sampleBuffers.has(summon.id)) {
      this._appSummonPlayed = true;
      this.playSample('appSummon', { serial: true });
    }
    this._flushPendingSamples();
  }

  _prepareSamples() {
    void this._decodeSamples().then(() => {
      // Do not call resume() here: decoding finishes outside the user gesture.
      // If iOS suspended meanwhile, the next actual playback retries it.
      this._playReadySounds();
    }).catch((error) => {
      console.warn('[scene:audio-decode]', error);
    });
  }

  async _resume() {
    const context = this._ensureContext();
    if (!context) return false;
    try {
      if (!await this._resumeContext(context)) return false;
      // Report the unlock immediately. Fetching and decoding several WAV files
      // is deliberately background work and must not delay the UI state change.
      this._playReadySounds();
      this._prepareSamples();
      return true;
    } catch {
      return false;
    }
  }

  async resume() {
    if (this._resumePromise) return this._resumePromise;
    const pending = this._resume();
    this._resumePromise = pending;
    try {
      return await pending;
    } finally {
      if (this._resumePromise === pending) this._resumePromise = null;
    }
  }

  _ensurePlaybackReady(retry) {
    if (!this.context || this.muted) return false;
    if (this.context.state === 'running') return true;
    // A previously unlocked context may have been suspended by iOS while the
    // page was backgrounded. Retry resume before dropping the user's sound.
    void this.resume().then((ready) => {
      if (ready && !this.muted) retry();
    });
    return false;
  }

  _playPlaceholderSample(key) {
    if (!this.context || this.context.state !== 'running' || this.muted) return false;
    if (key === 'appSummon' || key === 'yinYang') {
      return this._playCue(REFERENCE_CUES.awaken, 'yunluo', 0, 0.18, 0.72);
    }
    if (key === 'expand') {
      return this._playCue(REFERENCE_CUES.planeExpand, 'yunluo', 0, 0.22, 0.68);
    }
    if (key === 'formation') {
      const at = this.context.currentTime + AUDIO.scheduleAhead;
      REFERENCE_CHORD.forEach((note, index) => {
        const pan = (index / Math.max(1, REFERENCE_CHORD.length - 1) * 2 - 1) * 0.2;
        this._modalTone(at, note, 'bianzhong', pan, 0.58);
      });
      return true;
    }
    if (key === 'transform') {
      const at = this.context.currentTime + AUDIO.scheduleAhead;
      this._wind(at, 0.62, AUDIO.windStartFrequency, AUDIO.windEndFrequency, AUDIO.windGain);
      this._gong(at + 0.08, AUDIO.vortexGongFrequency, 0.72, 0.12);
      return true;
    }
    if (key === 'retract') {
      return this._playCue(REFERENCE_CUES.reset, 'yunluo', 0, 0.18, 0.62);
    }
    return false;
  }

  playSample(key, { serial = false } = {}) {
    const sample = DEMO_SAMPLES[key];
    const call = `sample:${key}`;
    if (!sample) return this._recordPlayback(call, 'missing-sample');
    if (!this.context) return this._recordPlayback(call, 'no-context');
    if (this.muted) return this._recordPlayback(call, 'muted');
    if (!this._ensurePlaybackReady(() => this.playSample(key, { serial }))) {
      return this._recordPlayback(call, 'resume-pending');
    }
    const buffer = this._sampleBuffers.get(sample.id);
    if (!buffer) {
      // 手机首次触摸后 WAV 还在解码时，当场播程序化占位音。
      // 不排队旧动作，避免 WAV 准备好后声音与画面错位。
      const placeholderStarted = this._playPlaceholderSample(key);
      return this._recordPlayback(
        call,
        placeholderStarted ? 'placeholder-started' : 'decode-pending',
        placeholderStarted
      );
    }
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    const earliest = this.context.currentTime + AUDIO.scheduleAhead;
    const at = serial ? Math.max(earliest, this._nextSampleAt) : earliest;
    source.buffer = buffer;
    gain.gain.setValueAtTime(sample.volume, at);
    source.connect(gain);
    gain.connect(this.sampleInput);
    this._trackSource(source);
    source.start(at);
    if (serial) this._nextSampleAt = at + buffer.duration + AUDIO.sampleGap;
    return this._recordPlayback(call, 'started', true);
  }

  setMuted(muted) {
    this.muted = Boolean(muted);
    try {
      globalThis.localStorage?.setItem(AUDIO.storageKey, this.muted ? '1' : '0');
    } catch {
      // Safari 隐私模式可能拒绝持久化；本次页面内仍照常静音。
    }
    if (this.context && this.master) {
      const now = this.context.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setTargetAtTime(
        this.muted ? AUDIO.minimumGain : AUDIO.masterGain,
        now,
        AUDIO.muteTimeConstant
      );
    }
    return this.muted;
  }

  toggleMuted() {
    return this.setMuted(!this.muted);
  }

  _connectWithPan(node, pan = 0) {
    if (!this.input) return;
    if (typeof this.context.createStereoPanner !== 'function') {
      node.connect(this.input);
      return;
    }
    const panner = this.context.createStereoPanner();
    panner.pan.value = pan;
    node.connect(panner);
    panner.connect(this.input);
  }

  _noiseBuffer(duration) {
    if (!this.context) return null;
    const samples = Math.max(1, Math.round(this.context.sampleRate * duration));
    const key = `${samples}`;
    if (this._noiseCache.has(key)) return this._noiseCache.get(key);
    const buffer = this.context.createBuffer(1, samples, this.context.sampleRate);
    buffer.copyToChannel(deterministicNoise(samples), 0);
    this._noiseCache.set(key, buffer);
    return buffer;
  }

  _strike(at, volume, pan = 0, frequency = AUDIO.strikeFrequency) {
    if (!this.context || !this.input || this.muted) return;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const envelope = this.context.createGain();
    source.buffer = this._noiseBuffer(AUDIO.strikeDuration);
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(frequency, at);
    filter.Q.value = AUDIO.strikeQ;
    envelope.gain.setValueAtTime(Math.max(AUDIO.minimumGain, volume), at);
    envelope.gain.exponentialRampToValueAtTime(AUDIO.minimumGain, at + AUDIO.strikeDuration);
    source.connect(filter);
    filter.connect(envelope);
    this._connectWithPan(envelope, pan);
    this._trackSource(source);
    source.start(at);
    source.stop(at + AUDIO.strikeDuration);
  }

  _modalTone(at, note, instrument = 'qing', pan = 0, volumeScale = 1) {
    if (!this.context || !this.input || this.muted) return false;
    const modes = INSTRUMENTS[instrument] || INSTRUMENTS.qing;
    modes.forEach((mode) => {
      const duration = Math.max(
        0.08,
        note.duration * mode.decay * AUDIO.modalTailScale
      );
      const oscillator = this.context.createOscillator();
      const envelope = this.context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(note.frequency * mode.ratio, at);
      oscillator.detune.setValueAtTime(mode.detune, at);
      oscillator.detune.linearRampToValueAtTime(mode.detune * 0.35, at + duration);
      envelope.gain.setValueAtTime(AUDIO.minimumGain, at);
      envelope.gain.exponentialRampToValueAtTime(
        Math.max(AUDIO.minimumGain, note.volume * mode.gain * volumeScale),
        at + AUDIO.modalAttack
      );
      envelope.gain.exponentialRampToValueAtTime(AUDIO.minimumGain, at + duration);
      oscillator.connect(envelope);
      this._connectWithPan(envelope, pan);
      this._trackSource(oscillator);
      oscillator.start(at);
      oscillator.stop(at + duration + AUDIO.modalReleaseTail);
    });
    this._strike(at, note.volume * AUDIO.strikeGain * volumeScale, pan);
    return true;
  }

  _playCue(cues, instrument, delay = 0, panWidth = 0.22, volumeScale = 1) {
    if (!this.context || this.muted) return false;
    if (!this._ensurePlaybackReady(
      () => this._playCue(cues, instrument, delay, panWidth, volumeScale)
    )) return false;
    const at = this.context.currentTime + AUDIO.scheduleAhead + delay;
    const denominator = Math.max(1, cues.length - 1);
    cues.forEach((note, index) => {
      const pan = ((index / denominator) * 2 - 1) * panWidth;
      this._modalTone(at + note.offset, note, instrument, pan, volumeScale);
    });
    return true;
  }

  _wind(at, duration, fromFrequency, toFrequency, volume = AUDIO.windGain) {
    if (!this.context || !this.input || this.muted) return false;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const envelope = this.context.createGain();
    source.buffer = this._noiseBuffer(duration);
    filter.type = 'bandpass';
    filter.Q.value = 0.58;
    filter.frequency.setValueAtTime(fromFrequency, at);
    filter.frequency.exponentialRampToValueAtTime(toFrequency, at + duration);
    envelope.gain.setValueAtTime(AUDIO.minimumGain, at);
    envelope.gain.exponentialRampToValueAtTime(volume, at + duration * 0.22);
    envelope.gain.exponentialRampToValueAtTime(AUDIO.minimumGain, at + duration);
    source.connect(filter);
    filter.connect(envelope);
    this._connectWithPan(envelope, 0);
    this._trackSource(source);
    source.start(at);
    source.stop(at + duration);
    return true;
  }

  _gong(at, frequency, duration, volume, pan = 0) {
    const note = { frequency, duration, volume };
    this._modalTone(at, note, 'gong', pan, 1);
    this._strike(at, volume * 0.46, pan, 1180);
  }

  playAwaken() {
    return this.playSample('yinYang', { serial: false });
  }

  playCastNote(position) {
    const call = `cast:${position}`;
    if (!this.context) return this._recordPlayback(call, 'no-context');
    if (this.muted) return this._recordPlayback(call, 'muted');
    const note = REFERENCE_NOTES[position - 1];
    if (!note) return this._recordPlayback(call, 'invalid-position');
    if (!this._ensurePlaybackReady(() => this.playCastNote(position))) {
      return this._recordPlayback(call, 'resume-pending');
    }
    const pan = (position - 3.5) * AUDIO.castPan;
    const started = this._modalTone(
      this.context.currentTime + AUDIO.scheduleAhead,
      note,
      'qing',
      pan,
      CAST_VOLUME_SCALE
    );
    return this._recordPlayback(call, started ? 'started' : 'failed', started);
  }

  playFormedChord(delay = AUDIO.formedDelay) {
    const call = 'formed-chord';
    if (!this.context) return this._recordPlayback(call, 'no-context');
    if (this.muted) return this._recordPlayback(call, 'muted');
    if (!this._ensurePlaybackReady(() => this.playFormedChord(delay))) {
      return this._recordPlayback(call, 'resume-pending');
    }
    const at = this.context.currentTime + AUDIO.scheduleAhead + delay;
    REFERENCE_CHORD.forEach((note, index) => {
      const pan = (index / Math.max(1, REFERENCE_CHORD.length - 1) * 2 - 1) * 0.24;
      this._modalTone(at, note, 'bianzhong', pan, 0.72);
    });
    return this._recordPlayback(call, 'started', true);
  }

  playExpand() {
    // 展开声必须和 EXPANDING 画面同帧，不等阴阳显现尾音播完。
    return this.playSample('expand', { serial: false });
  }

  playFormationTransition() {
    // 成卦过渡必须跟第六爻定格同步，不受前一段 demo 尾音队列延迟。
    return this.playSample('formation', { serial: false });
  }

  playVortex() {
    return this.playSample('transform', { serial: true });
  }

  playCollapse() {
    return this.playSample('retract', { serial: true });
  }

  playReset() {
    return this.playSample('retract', { serial: true });
  }

  playPreview() {
    const call = 'preview';
    if (!this.context) return this._recordPlayback(call, 'no-context');
    if (this.muted) return this._recordPlayback(call, 'muted');
    if (!this._ensurePlaybackReady(() => this.playPreview())) {
      return this._recordPlayback(call, 'resume-pending');
    }
    const started = this._modalTone(
      this.context.currentTime + AUDIO.scheduleAhead,
      REFERENCE_NOTES[0],
      'qing',
      0,
      AUDIO.previewVolumeRatio
    );
    return this._recordPlayback(call, started ? 'started' : 'failed', started);
  }

  handleDirectorEvent(name, payload = {}) {
    if (!this.context || this.muted) return null;
    if (name === 'state' && payload.state === 'aim') {
      this._beginAction();
      this.playAwaken();
      return 'demo15-yin-yang';
    }
    if (name === 'state' && payload.state === 'expanding') {
      this._beginAction();
      this.playExpand();
      return 'demo19-expand';
    }
    if (name === 'state' && payload.state === 'dormant' && !payload.mode) {
      this._beginAction();
      this.playReset();
      return 'demo36-retract';
    }
    if (name === 'line') {
      this._beginAction();
      this.playCastNote(payload.index);
      return 'cast-qing';
    }
    if (name === 'formation-transition') {
      this._beginAction();
      this.playFormationTransition();
      return 'demo1-formation-transition';
    }
    if (name === 'formed') {
      this._beginAction();
      this.playFormedChord(0);
      return 'formed-chord';
    }
    if (name === 'transform') {
      this._beginAction();
      this.playVortex();
      return 'demo21-transform';
    }
    if (name === 'collapse') {
      this._beginAction();
      this.playCollapse();
      return 'demo36-retract';
    }
    return null;
  }
}
